import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import AltRouteOutlinedIcon from '@mui/icons-material/AltRouteOutlined';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LockIcon from '@mui/icons-material/Lock';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import SpeedIcon from '@mui/icons-material/Speed';
import DescriptionIcon from '@mui/icons-material/Description';
import BuildIcon from '@mui/icons-material/Build';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import FeedbackIcon from '@mui/icons-material/Feedback';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PrintIcon from '@mui/icons-material/Print';
import { useTranslation } from 'react-i18next';
import { useCRUD } from '../../../../api/crud';
import {
  JOB_STATUS_TRANSITION_ACTIONS_ENDPOINT,
  getJobStatusLabelKey,
  JOB_STATUS_COLOR_MAP,
  JOB_KIND_COLOR_MAP,
  getJobKindLabelKey,
} from '../../../../consts';
import {
  StatusTransitionRequiredAction,
  StatusTransitionRequiredField,
  StatusTransitionServiceRequirement,
} from '../../../../types/jobs';
import { useHasPermission } from '../../../../contexts/auth';
import { useBreadcrumbs } from '../../../../components/ui/breadcrumps/breadcrumbsContext';
import { TENANT_ROUTES } from '../../tenantConfig.routes';
import {
  DetailHeader,
  DetailPage,
  DetailRow,
  DetailSection,
} from '../../../../components/ui/detailView';
import { StatusChip } from '../../../../components/ui/statusChip/statusChip.component';

const FIELD_TYPE_ICONS: Record<string, React.ReactElement> = {
  C: <CheckBoxIcon fontSize="small" />,
  V: <SpeedIcon fontSize="small" />,
  T: <DescriptionIcon fontSize="small" />,
  S: <BuildIcon fontSize="small" />,
  I: <ReportProblemIcon fontSize="small" />,
  U: <Inventory2Icon fontSize="small" />,
  R: <FeedbackIcon fontSize="small" />,
  A: <AttachFileIcon fontSize="small" />,
  N: <WarningAmberIcon fontSize="small" />,
  E: <PrintIcon fontSize="small" />,
};

export const TenantStatusTransitionActionDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get } = useCRUD(JOB_STATUS_TRANSITION_ACTIONS_ENDPOINT);
  const { setOverride, clearOverride } = useBreadcrumbs();
  const [action, setAction] = useState<StatusTransitionRequiredAction | null>(null);
  const canView = useHasPermission("service.view_jobstatustransitionaction");

  useEffect(() => {
    const listPath = TENANT_ROUTES.jobStatusActions;
    setOverride(listPath, t('Job status actions'));

    const fetchAction = async () => {
      if (id) {
        const result = await get(id);
        if (result?.success && result?.data) {
          setAction(result.data);
          const prevLabel = getJobStatusLabelKey(result.data.previous_status);
          const newLabel = getJobStatusLabelKey(result.data.new_status);
          setOverride(
            TENANT_ROUTES.jobStatusAction(id),
            `${prevLabel ? t(prevLabel) : result.data.previous_status} → ${newLabel ? t(newLabel) : result.data.new_status}`
          );
        } else {
          navigate(TENANT_ROUTES.jobStatusActions);
        }
      }
    };

    const handleUpdated = () => { fetchAction(); };
    window.addEventListener('statusTransitionActionsUpdated', handleUpdated);
    fetchAction();
    return () => {
      window.removeEventListener('statusTransitionActionsUpdated', handleUpdated);
      clearOverride(listPath);
      if (id) {
        clearOverride(TENANT_ROUTES.jobStatusAction(id));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, t]);

  if (!canView) return null;
  if (!action) return <DetailPage loading />;

  const prevLabelKey = getJobStatusLabelKey(action.previous_status);
  const newLabelKey = getJobStatusLabelKey(action.new_status);
  const FIELD_TYPE_LABELS: Record<string, string> = {
    C: t('Checkbox'),
    V: t('Counter values'),
    T: t('Description'),
    S: t('Services'),
    I: t('Issues'),
    U: t('Used parts'),
    R: t('Rejection reason'),
    A: t('Attachments'),
    N: t('Remarks'),
    E: t('Equipment'),
  };

  const serviceRequirements = action.service_requirements ?? [];
  const obligatoryFields = action.required_fields.filter((field) => field.is_required);
  const optionalFields = action.required_fields.filter((field) => !field.is_required);

  const renderFieldChip = (field: StatusTransitionRequiredField) => (
    <Chip
      key={field.id}
      icon={FIELD_TYPE_ICONS[field.field_type]}
      label={FIELD_TYPE_LABELS[field.field_type] ?? field.field_type}
      size="small"
      color={field.is_required ? 'warning' : 'default'}
      variant="outlined"
    />
  );

  const renderRequirementChip = (requirement: StatusTransitionServiceRequirement) => (
    <Chip
      icon={FIELD_TYPE_ICONS[requirement.field_type]}
      label={FIELD_TYPE_LABELS[requirement.field_type] ?? requirement.field_type}
      size="small"
      color="warning"
      variant="outlined"
    />
  );

  const chipList = (chips: React.ReactNode[], emptyText: string) =>
    chips.length > 0 ? (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>{chips}</Box>
    ) : (
      <Typography variant="body2" color="text.secondary" fontStyle="italic">
        {emptyText}
      </Typography>
    );

  const previousLabel = prevLabelKey ? t(prevLabelKey) : action.previous_status;
  const newLabel = newLabelKey ? t(newLabelKey) : action.new_status;

  return (
    <DetailPage>
      <DetailHeader
        icon={<AltRouteOutlinedIcon />}
        title={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <StatusChip label={previousLabel} value={action.previous_status} colorMap={JOB_STATUS_COLOR_MAP} />
            <ArrowForwardIcon fontSize="small" color="disabled" />
            <StatusChip label={newLabel} value={action.new_status} colorMap={JOB_STATUS_COLOR_MAP} />
          </Box>
        }
        chips={
          action.is_protected && (
            <StatusChip
              icon={<LockIcon fontSize="small" />}
              label={t('Protected default action')}
              color="info"
              variant="outlined"
            />
          )
        }
      />

      <DetailSection title={t('Basic Information')}>
        <DetailRow label={t('Obligatory fields')}>
          {chipList(obligatoryFields.map(renderFieldChip), t('No obligatory fields configured.'))}
        </DetailRow>
        <DetailRow label={t('Optional fields')}>
          {chipList(optionalFields.map(renderFieldChip), t('No optional fields configured.'))}
        </DetailRow>
        <DetailRow label={t('Applies to')}>
          {chipList(
            action.job_kinds.map((jk) => (
              <Chip
                key={jk.id}
                label={t(getJobKindLabelKey(jk.kind) ?? jk.kind)}
                color={JOB_KIND_COLOR_MAP[jk.kind] ?? 'default'}
                size="small"
              />
            )),
            t('Applies to all job kinds.')
          )}
        </DetailRow>
      </DetailSection>

      <DetailSection title={t('Requirements by service')}>
        {serviceRequirements.length > 0 ? (
          serviceRequirements.map((requirement) => (
            <DetailRow key={requirement.id} label={renderRequirementChip(requirement)}>
              {chipList(
                requirement.services.map((service) => (
                  <Chip key={service.id} label={service.title} size="small" />
                )),
                t('No services')
              )}
            </DetailRow>
          ))
        ) : (
          <DetailRow label={t('For tasks with services')}>
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              {t('No requirements by service.')}
            </Typography>
          </DetailRow>
        )}
      </DetailSection>
    </DetailPage>
  );
};
